import { Component, EventEmitter, forwardRef, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OverlayOptions } from 'primeng/api';
import { activeTranslations } from '../../enum/constants';
import { SelectType } from './select.interface';

@Component({
  selector: 'app-select',
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => SelectComponent),
    },
  ],
})
export class SelectComponent implements ControlValueAccessor {
  // Common
  @Input() id: string = '';
  @Input() label: string = '';
  @Input() labelType: 'default' | 'float' | 'ifta' = 'default';
  @Input() labelVariant: 'over' | 'in' | 'on' = 'over';
  @Input() placeholder: string = '';
  private inputDisabled = false;
  private controlDisabled = false;
  @Input() get disabled(): boolean {
    return this.inputDisabled || this.controlDisabled;
  }
  set disabled(value: boolean) {
    this.inputDisabled = value;
  }
  @Input() loading: boolean = false;
  @Input() required: boolean = false;
  @Input() error: boolean = false;
  @Input() errorMessage: string = '';
  @Input() customClass: string = '';
  @Input() formControl?: FormControl; // Optional FormControl for errors
  @Input() showErrors: boolean = true;
  @Input() validationMessages?: Record<string, string>;
  @Input() size!: 'small' | 'large';
  @Input() fluid?: boolean;
  @Input() isTranslate: boolean = false;
  @Input() isImg: boolean = false;
  @Input() isAvatar: boolean = false;

  // Data
  @Input() options: any[] = [];
  @Input() optionLabel: string = 'name';
  @Input() optionGroupLabel: string = 'name';
  @Input() optionGroupChildren: string | string[] = ['children'];
  @Input() optionValue?: string;
  @Input() optionDisabled: string | undefined;
  @Input() group: boolean = false;

  // Behavior
  @Input() type: SelectType = SelectType.Default;
  @Input() filter: boolean = false;
  @Input() searchInInput: boolean = false;
  @Input() showClear: boolean = false;
  @Input() resetFilterOnHide: boolean = true;
  @Input() appendTo: 'body' | null = 'body';
  @Input() overlayOptions: OverlayOptions = {};
  @Input() focusOnHover: boolean = true;
  @Input() selectOnFocus: boolean = false;
  @Input() autoOptionFocus: boolean = false;

  // Multi specifics
  @Input() display: 'chip' | 'comma' = 'chip';
  @Input() showToggleAll: boolean = true;
  @Input() maxSelectedLabels: number = 3;
  @Input() selectionLimit?: number;

  // Virtual scroll
  @Input() virtualScroll: boolean = false;
  @Input() itemSize: number = 34;

  // Messages
  // Живі дефолти: читаються при кожному зверненні, тож оновлюються зі зміною мови.
  private customEmptyMessage?: string;
  private customEmptyFilterMessage?: string;

  @Input() set emptyMessage(value: string) {
    this.customEmptyMessage = value;
  }
  get emptyMessage(): string {
    return this.customEmptyMessage ?? activeTranslations.nothing_found ?? 'Nothing found';
  }

  @Input() set emptyFilterMessage(value: string) {
    this.customEmptyFilterMessage = value;
  }
  get emptyFilterMessage(): string {
    return this.customEmptyFilterMessage ?? activeTranslations.searching ?? 'Type to search';
  }

  // Events
  @Output() valueChange = new EventEmitter<any>();
  @Output() optionSelected = new EventEmitter<any>();
  @Output() filterChange = new EventEmitter<string>();
  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() focus = new EventEmitter<void>();
  @Output() blur = new EventEmitter<void>();

  /**
   * p-select приймає optionGroupChildren лише як рядок (один рівень вкладеності),
   * тоді як p-cascadeSelect — масив ключів по рівнях.
   */
  protected get flatOptionGroupChildren(): string {
    return Array.isArray(this.optionGroupChildren) ? this.optionGroupChildren[0] : this.optionGroupChildren;
  }

  protected value: any = this.type === SelectType.Multiple ? [] : null;
  protected suggestions: any[] = [];

  // CVA hooks
  onChangeFn: (val: any) => void = () => {};
  onTouchedFn: () => void = () => {};

  writeValue(obj: any): void {
    this.value = obj;
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.controlDisabled = isDisabled;
  }

  protected handleChange(newValue: any): void {
    if (this.disabled) return;
    this.value = newValue;
    this.onChangeFn(newValue);
    this.valueChange.emit(newValue);
  }

  protected handleTouched(): void {
    this.formControl?.markAsTouched();
    this.onTouchedFn();
  }

  protected handleFilter(event: any): void {
    const term = typeof event === 'string' ? event : (event?.filter ?? '');
    this.filterChange.emit(term);
  }

  protected handleAutoComplete(event: { query?: string }): void {
    const query = String(event?.query ?? '')
      .trim()
      .toLocaleLowerCase();
    this.suggestions = this.options.filter((option) => {
      const label = typeof option === 'object' && option !== null ? option[this.optionLabel] : option;
      return String(label ?? '')
        .toLocaleLowerCase()
        .includes(query);
    });
  }

  protected handleOptionSelected(event: any): void {
    this.optionSelected.emit(event?.value);
  }

  protected get hasError(): boolean {
    const ctrlInvalid = !!this.formControl?.invalid && (this.formControl?.touched || this.formControl?.dirty);
    return this.error || (this.showErrors && ctrlInvalid);
  }

  protected get computedErrorMessage(): string | null {
    if (!this.hasError) return null;
    if (this.errorMessage) return this.errorMessage;
    const errors = this.formControl?.errors || {};
    const firstKey = Object.keys(errors)[0];
    if (!firstKey) return null;
    if (this.validationMessages && this.validationMessages[firstKey]) {
      return this.validationMessages[firstKey];
    }
    // Fallback generic messages
    const fallback: Record<string, string> = {
      required: 'Field is required',
      minlength: 'Value is too short',
      maxlength: 'Value is too long',
    };
    return fallback[firstKey] || 'Invalid value';
  }

  protected get errorI18nKey(): string | null {
    if (!this.hasError) return null;
    if (this.errorMessage) return null;

    const errors = this.formControl?.errors;
    if (!errors) return null;

    const first = Object.keys(errors)[0];
    if (!first) return null;

    return `input_errors.${first}`;
  }

  protected get errorI18nParams(): Record<string, any> | undefined {
    if (!this.hasError || !this.formControl?.errors) return undefined;
    const e = this.formControl.errors;
    if ('maxlength' in e) return { value: e['maxlength']?.requiredLength };
    if ('minlength' in e) return { value: e['minlength']?.requiredLength };
    if ('max' in e) return { value: e['max']?.max };
    if ('min' in e) return { value: e['min']?.min };
    return undefined;
  }

  protected readonly SelectType = SelectType;
}
